import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import MoreHorizOutlined from '@material-ui/icons/MoreHorizOutlined';
import React from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { Space } from 'v2/components/Shared/Space';
import Truncate from 'v2/components/Shared/TruncateText';
import { useSubscriptionPlans } from 'v2/hooks/useSubscriptionPlan';

import type { SubscriptionPlan } from 'v2/interfaces/product';

import { BasePlanTag } from './BasePlanTag';
import { SubscriptionFrequency } from './constants';
import { useStyles } from './style';

import type IProduct from 'interfaces/IProduct';

type ListSubscriptionPlanProps = {
  product: IProduct;
  handleActionsClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, plan: SubscriptionPlan) => void;
};

function TableState({ message }: { message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={6} align='center' style={{ padding: 16 }}>
        {message}
      </TableCell>
    </TableRow>
  );
}

const ListSubscriptionPlanComponent = ({ product, handleActionsClick }: ListSubscriptionPlanProps) => {
  const classes = useStyles();

  const { isLoading, data, isError } = useSubscriptionPlans(product.id, {
    enabled: !!product.id,
  });

  async function handleCopyLinkClick(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado para a área de transferência');
    } catch (error) {
      toast.error('Erro ao copiar o link');
      console.error(error);
    }
  }

  return (
    <TableContainer>
      <Table size='small' className={classes.table}>
        <TableHead>
          <TableCell>Plano</TableCell>
          <TableCell width={100}>Frequência</TableCell>
          <TableCell width={200}>Valor</TableCell>
          <TableCell align='center' width={140}>
            Troca de plano
          </TableCell>
          <TableCell>Página de pagamento</TableCell>
          <TableCell width={75} align='center'>
            Ações
          </TableCell>
        </TableHead>
        <TableBody>
          {isLoading && <TableState message='Carregando...' />}
          {!isLoading && !data?.length && !isError && <TableState message='Nenhum plano cadastrado para este produto' />}
          {isError && <TableState message='Ocorreu um erro ao carregar os planos' />}
          {data?.map((row, index) => (
            <TableRow className={classes.tableRow} key={row.plan_id}>
              <TableCell
                style={{
                  whiteSpace: 'nowrap',
                }}
              >
                <Truncate count={20} style={{ display: 'inline-block' }}>
                  {row.plan_title}
                </Truncate>
                {row.plan_is_default && (
                  <>
                    <Space
                      axis='horizontal'
                      size={8}
                      style={{
                        display: 'inline-block',
                      }}
                    />
                    <BasePlanTag />
                  </>
                )}
              </TableCell>
              <TableCell>{SubscriptionFrequency[row.plan_frequencia]}</TableCell>
              <TableCell>
                <FormattedNumber value={row.plan_value || 0} format='BRL' />
              </TableCell>
              <TableCell align='center'>
                <Icon
                  style={{
                    width: 100,
                    marginTop: 4,
                    color: row.plan_trocaplanoproduto || row.plan_trocaplanovariacao ? 'limegreen' : 'gray',
                    opacity: row.plan_trocaplanoproduto || row.plan_trocaplanovariacao ? 1 : 0.5,
                  }}
                >
                  task_alt
                </Icon>
              </TableCell>
              <TableCell>
                <span style={{ display: 'inline-block', minWidth: 270 }}>{row.plan_url_checkout}</span>
                <IconButton onClick={() => handleCopyLinkClick(row.plan_url_checkout)}>
                  <FileCopyOutlined style={{ fontSize: 18 }} />
                </IconButton>
              </TableCell>
              <TableCell align='center'>
                <IconButton id={`plan-actions-${index}`} size='small' onClick={(e) => handleActionsClick(e, row)}>
                  <MoreHorizOutlined />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ListSubscriptionPlan = React.memo(ListSubscriptionPlanComponent);

export { ListSubscriptionPlan };
